import React from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Switch } from '@/components/ui/switch';
import { StatusBadgeDisplay } from '@/components/ui/status-badge-display';
import { useStickyHeader } from '@/hooks/useStickyHeader';
import { cn } from '@/lib/utils';

interface FormHeaderWithStatusProps<
	TFieldValues extends FieldValues = FieldValues
> {
	/**
	 * @deprecated Title is no longer displayed in the header. Use page header context instead.
	 */
	title?: string;
	/**
	 * @deprecated Back button is now in the page header. Use page header context showBackButton instead.
	 */
	onBack?: () => void;
	/**
	 * Content to display on the right side of the sticky header (e.g., submit button)
	 */
	rightContent?: React.ReactNode;
	// Status field props
	control?: Control<TFieldValues>;
	isActive?: boolean;
	/**
	 * Enable sticky behavior - header will stick to top when scrolling
	 * @default true
	 */
	sticky?: boolean;
}

/**
 * Form header component with sticky back button and submit button in header.
 * Status control (when provided) is shown in a separate row below the sticky header.
 */
export const FormHeaderWithStatus = <
	TFieldValues extends FieldValues = FieldValues
>({
	title: _title,
	onBack: _onBack,
	rightContent,
	control,
	isActive,
	sticky = true
}: FormHeaderWithStatusProps<TFieldValues>) => {
	const { t } = useTranslation();
	const { isSticky, headerRef, sentinelRef } = useStickyHeader({ threshold: 0 });
	const shouldBeSticky = sticky && isSticky;

	// Status content to be rendered in a separate row below the header
	const statusContent = control ? (
		<Controller
			control={control}
			name={'isActive' as Path<TFieldValues>}
			render={({ field }) => (
				<div className="flex items-center gap-2">
					<span className="text-sm font-medium">
						{t('form.status')}
					</span>
					<Switch
						checked={field.value as boolean}
						onCheckedChange={field.onChange}
					/>
					<span
						className={cn(
							'text-sm min-w-[60px]',
							(field.value as boolean) ? 'font-bold text-foreground' : 'text-muted-foreground'
						)}
					>
						{(field.value as boolean)
							? t('status.active')
							: t('status.inactive')}
					</span>
				</div>
			)}
		/>
	) : isActive !== undefined ? (
		<StatusBadgeDisplay
			isActive={isActive}
			statusLabel={t('form.status')}
			activeLabel={t('status.active')}
			inactiveLabel={t('status.inactive')}
		/>
	) : null;

	return (
		<>
			{/* Sentinel element - marks the original position of the header */}
			<div ref={sentinelRef} className="h-0 w-full" aria-hidden="true" />

			{/* Sticky header container */}
			<div
				ref={headerRef}
				className={cn(
					'w-full z-20',
					shouldBeSticky
						? 'sticky top-0 py-3 backdrop-blur-xl bg-white/20 dark:bg-gray-900/20 border-b border-white/20 dark:border-gray-700/20 shadow-[0_4px_30px_rgba(0,0,0,0.03)]'
						: 'mb-6'
				)}
			>
				<div className="flex items-center justify-between w-full">
					{/* Left side: submit button */}
					<div className="flex items-center gap-3">
						{rightContent}
					</div>
					{/* Right side: status toggle */}
					{statusContent && (
						<div className="flex items-center gap-4 flex-shrink-0">
							{statusContent}
						</div>
					)}
				</div>
			</div>
		</>
	);
};
